/**
 * Dialog de migration d'une pièce du Dossier répressif (PdfFile) vers
 * un autre bucket : Pièces client / Pièces adverses / Conclusions
 * déposées / Autres documents (Sprint 3+, 2026-05-21).
 *
 * Workflow Maxim : drop tout dans Pièces, puis redistribue post-OCR.
 *
 * Préservation des dépendances :
 * - DR → Pièces client : OCR + annotations + bookmarks préservés (XOR FK)
 * - DR → autres : OCR text copié, annotations/bookmarks perdus
 *
 * Avant la migration on affiche un compteur des dépendances qui seront
 * perdues (modal de confirmation éclairée).
 */
import { useCallback, useEffect, useState } from 'react';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

type TargetType = 'piece_client' | 'piece_adverse' | 'other_document' | 'conclusion_file';

type Dependencies = {
  ocr_pages: number;
  annotations: number;
  bookmarks: number;
  embeddings: number;
};

const TARGETS: { code: TargetType; label: string; description: string }[] = [
  {
    code: 'piece_client',
    label: 'Pièces client',
    description: 'Pièce apportée par le client. RAG + annotations + bookmarks préservés.',
  },
  {
    code: 'piece_adverse',
    label: 'Conclusions et pièces adverses',
    description:
      'Pièce communiquée par PC / MP / co-prévenu. RAG + annotations + bookmarks préservés (essentiel pour la stratégie de réplique).',
  },
  {
    code: 'conclusion_file',
    label: 'Nos conclusions (déposée)',
    description: 'Conclusion effectivement déposée. Demande audience + date dépôt.',
  },
  {
    code: 'other_document',
    label: 'Autres documents',
    description: 'Sort du RAG (courrier, note, facture). Annotations/OCR perdus.',
  },
];

type Props = {
  pdfId: string;
  pdfTitle: string;
  onClose: () => void;
  onMigrated: () => void;
};

export default function MigratePieceDialog({ pdfId, pdfTitle, onClose, onMigrated }: Props) {
  const [target, setTarget] = useState<TargetType>('piece_client');
  const [deps, setDeps] = useState<Dependencies | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Champs target-specific
  const [titre, setTitre] = useState('');
  const [paSource, setPaSource] = useState<
    'partie_civile' | 'ministere_public' | 'co_prevenu' | 'autre'
  >('partie_civile');
  const [paStatut, setPaStatut] = useState<'recue' | 'attendue' | 'contestee'>('recue');
  const [paAuthorLabel, setPaAuthorLabel] = useState('');
  const [odKind, setOdKind] = useState<
    'courrier' | 'note' | 'facture' | 'convention_honoraires' | 'autre'
  >('courrier');
  const [cfOrigine, setCfOrigine] = useState<
    'concluant' | 'mp' | 'partie_civile' | 'autre_partie' | 'autre'
  >('concluant');
  const [cfStatut, setCfStatut] = useState<
    'draft' | 'a_deposer' | 'deposee' | 'recue' | 'archivee'
  >('deposee');
  const [cfAudience, setCfAudience] = useState('');
  const [cfDateDepot, setCfDateDepot] = useState(new Date().toISOString().slice(0, 10));

  const loadDeps = useCallback(async () => {
    try {
      const d = await api.get<Dependencies>(`/pieces-migration/dependencies/${pdfId}`);
      setDeps(d);
    } catch (err) {
      console.error('Failed to load deps', err);
    }
  }, [pdfId]);

  useEffect(() => {
    void loadDeps();
  }, [loadDeps]);

  const onSubmit = async () => {
    setSubmitting(true);
    const body: Record<string, unknown> = { target_type: target };
    if (titre.trim()) body.titre = body.title = titre.trim();
    if (target === 'piece_adverse') {
      body.source = paSource;
      body.statut = paStatut;
      if (paAuthorLabel.trim()) body.author_label = paAuthorLabel.trim();
    } else if (target === 'other_document') {
      body.kind = odKind;
    } else if (target === 'conclusion_file') {
      body.origine = cfOrigine;
      body.statut = cfStatut;
      if (cfAudience.trim()) body.audience_visee = cfAudience.trim();
      if (cfDateDepot) body.date_depot = cfDateDepot;
    }
    try {
      await api.post<{ target_type: string; target_id: string }>(
        `/pieces-migration/pdf/${pdfId}/migrate`,
        body
      );
      toast.success(`Migré vers ${TARGETS.find((t) => t.code === target)!.label}`);
      onMigrated();
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Migration cc88dd99eeff (2026-05-21) : PieceClient ET PieceAdverse
  // préservent tout (XOR FK partagé). Seuls OtherDocument et
  // ConclusionFile sortent du RAG / perdent les annotations.
  const FULL_PRESERVE_TARGETS = new Set<TargetType>(['piece_client', 'piece_adverse']);
  const isFullPreserve = FULL_PRESERVE_TARGETS.has(target);
  const willLoseAnnotations = !isFullPreserve && deps && deps.annotations > 0;
  const willLoseBookmarks = !isFullPreserve && deps && deps.bookmarks > 0;
  const willLoseEmbeddings = !isFullPreserve && deps && deps.embeddings > 0;
  const ocrPreserved = isFullPreserve;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-md bg-paper-elevated p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-[16px] font-medium text-text-primary">
          Migrer la pièce vers…
        </h3>
        <p className="mt-1 truncate text-[12px] text-text-secondary" title={pdfTitle}>
          {pdfTitle}
        </p>

        <div className="mt-4 space-y-2">
          {TARGETS.map((t) => (
            <label
              key={t.code}
              className={`flex cursor-pointer items-start gap-2 rounded-md border p-2.5 transition ${
                target === t.code
                  ? 'border-accent bg-accent-light'
                  : 'border-rule hover:border-rule-strong'
              }`}
            >
              <input
                type="radio"
                name="target"
                value={t.code}
                checked={target === t.code}
                onChange={() => setTarget(t.code)}
                className="mt-1"
              />
              <div className="flex-1 text-[12.5px]">
                <p className="font-medium text-text-primary">{t.label}</p>
                <p className="mt-0.5 text-text-secondary">{t.description}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Warning sur dépendances */}
        {deps && (willLoseAnnotations || willLoseBookmarks || willLoseEmbeddings) && (
          <div className="mt-4 flex items-start gap-2 rounded-md border border-cat-imp/40 bg-cat-imp-pastel p-3 text-[12px] text-cat-imp-text">
            <IconAlertTriangle size={16} stroke={1.7} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Données perdues à la migration :</p>
              <ul className="mt-1 list-disc pl-4">
                {willLoseAnnotations && <li>{deps.annotations} annotation(s)</li>}
                {willLoseBookmarks && <li>{deps.bookmarks} marqueur(s)</li>}
                {willLoseEmbeddings && (
                  <li>{deps.embeddings} embedding(s) RAG (réindexation requise si retour DR)</li>
                )}
              </ul>
              <p className="mt-1">OCR text préservé sous forme de texte brut.</p>
            </div>
          </div>
        )}
        {deps && ocrPreserved && (deps.ocr_pages > 0 || deps.annotations > 0) && (
          <div className="mt-4 rounded-md border border-cat-fav/40 bg-cat-fav-pastel p-3 text-[12px] text-cat-fav">
            <p className="font-medium">Données préservées :</p>
            <ul className="mt-1 list-disc pl-4">
              <li>{deps.ocr_pages} page(s) OCR re-pointée(s)</li>
              <li>{deps.annotations} annotation(s) conservée(s)</li>
              <li>{deps.bookmarks} marqueur(s) conservé(s)</li>
            </ul>
          </div>
        )}

        {/* Champ titre commun */}
        <div className="mt-4">
          <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
            Titre (laisser vide pour reprendre le titre actuel)
          </label>
          <input
            type="text"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
          />
        </div>

        {/* Champs target-specific */}
        {target === 'piece_adverse' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Source
              </label>
              <select
                value={paSource}
                onChange={(e) => setPaSource(e.target.value as typeof paSource)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                <option value="partie_civile">Partie civile</option>
                <option value="ministere_public">Ministère public</option>
                <option value="co_prevenu">Co-prévenu</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Statut
              </label>
              <select
                value={paStatut}
                onChange={(e) => setPaStatut(e.target.value as typeof paStatut)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                <option value="recue">Reçue</option>
                <option value="attendue">Attendue</option>
                <option value="contestee">Contestée</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Auteur (libellé)
              </label>
              <input
                type="text"
                value={paAuthorLabel}
                onChange={(e) => setPaAuthorLabel(e.target.value)}
                placeholder="ex. Maître Dupont, conseil de M. Untel"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
          </div>
        )}

        {target === 'other_document' && (
          <div className="mt-3">
            <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
              Type
            </label>
            <select
              value={odKind}
              onChange={(e) => setOdKind(e.target.value as typeof odKind)}
              className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
            >
              <option value="courrier">Courrier</option>
              <option value="note">Note</option>
              <option value="facture">Facture</option>
              <option value="convention_honoraires">Convention d'honoraires</option>
              <option value="autre">Autre</option>
            </select>
          </div>
        )}

        {target === 'conclusion_file' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Origine
              </label>
              <select
                value={cfOrigine}
                onChange={(e) => setCfOrigine(e.target.value as typeof cfOrigine)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                <option value="concluant">Concluant (M24)</option>
                <option value="mp">Ministère public</option>
                <option value="partie_civile">Partie civile</option>
                <option value="autre_partie">Autre partie</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Statut
              </label>
              <select
                value={cfStatut}
                onChange={(e) => setCfStatut(e.target.value as typeof cfStatut)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              >
                <option value="deposee">Déposée</option>
                <option value="a_deposer">À déposer</option>
                <option value="draft">Brouillon</option>
                <option value="recue">Reçue</option>
                <option value="archivee">Archivée</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Audience visée
              </label>
              <input
                type="text"
                value={cfAudience}
                onChange={(e) => setCfAudience(e.target.value)}
                placeholder="ex. CC 03/06, fond 12/09"
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.5px] text-text-tertiary">
                Date dépôt
              </label>
              <input
                type="date"
                value={cfDateDepot}
                onChange={(e) => setCfDateDepot(e.target.value)}
                className="mt-1 w-full rounded-md border border-rule-strong bg-paper-elevated px-2 py-1.5 text-[13px]"
              />
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Btn type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Annuler
          </Btn>
          <Btn
            type="button"
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={submitting}
          >
            <IconArrowRight size={14} stroke={1.6} />
            {submitting ? 'Migration…' : 'Migrer'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
